/** @format */

import {sensorName} from 'helpers/sensor_names';

import {TanStackTableData} from 'components/Table/core/types';

import {ResolvedCellData} from '../components/ResolvedCell';
import {barWidth, isNotNull, resolvedBarColor} from '../helpers';
import {CompanySensorsMetricsFromQuery} from '../types';

const parentBar = (value: number) => ({width: value, color: resolvedBarColor(value)});

const subBar = (value: number, max: number) => ({
  width: barWidth(value, max),
  color: resolvedBarColor(value),
});

export const mapSummaryData = (
  metrics: ReadonlyArray<CompanySensorsMetricsFromQuery>,
): TanStackTableData[] =>
  metrics.filter(isNotNull).map(metric => {
    const sensors = (metric.sensors ?? []).filter(isNotNull);
    const activeSensors = sensors.filter(sensor => !sensor.isDisabled);

    const minValues = activeSensors.map(sensor => sensor.resolvedMin);
    const maxValues = activeSensors.map(sensor => sensor.resolvedMax);
    const min = minValues.length ? Math.min(...minValues) : 1;
    const max = maxValues.length ? Math.max(...maxValues) : 1;

    const avgSum = activeSensors.reduce((acc, sensor) => acc + sensor.resolvedAvg, 0);
    const avg = sensors.length ? Math.round(avgSum / sensors.length) : 0;

    const foundTotal = sensors.reduce((acc, sensor) => acc + sensor.found, 0);
    const fixedTotal = sensors.reduce((acc, sensor) => acc + sensor.fixed, 0);
    const fixedPercent = foundTotal > 0 ? Math.round((fixedTotal / foundTotal) * 100) : 0;

    const names = (metric.connectedUsers ?? [])
      .filter(isNotNull)
      .map(user => user.profile?.name ?? '');

    const subRows: TanStackTableData[] = sensors.map((sensor, index) => {
      const sensorFixedPercent =
        sensor.found > 0 ? Math.round((sensor.fixed / sensor.found) * 100) : 0;

      const avgCell: ResolvedCellData = {
        display: sensor.isDisabled ? '' : sensor.resolvedAvg,
        bar: sensor.isDisabled ? null : subBar(sensor.resolvedAvg, max),
      };
      const minCell: ResolvedCellData = {
        display: sensor.resolvedMin,
        bar: sensor.isDisabled ? null : subBar(sensor.resolvedMin, max),
      };
      const maxCell: ResolvedCellData = {
        display: sensor.resolvedMax,
        bar: sensor.isDisabled ? null : subBar(sensor.resolvedMax, max),
      };

      return {
        id: `${metric.company.id}::sub::${index}`,
        isDisabled: sensor.isDisabled,
        hasExceptions: sensor.hasExceptions,
        company: '',
        staff: 0,
        staffNames: [],
        sensors: sensorName(sensor.name),
        found: sensor.isDisabled ? undefined : sensor.found,
        fixed: sensor.isDisabled ? undefined : sensor.fixed,
        fixedPercent: sensor.isDisabled ? undefined : sensorFixedPercent,
        resolvedAvg: sensor.isDisabled ? undefined : sensor.resolvedAvg,
        resolvedMin: sensor.isDisabled ? undefined : sensor.resolvedMin,
        resolvedMax: sensor.isDisabled ? undefined : sensor.resolvedMax,
        avgCell,
        minCell,
        maxCell,
      } as unknown as TanStackTableData;
    });

    const hasActiveSensors = activeSensors.length > 0;
    const emptyCell: ResolvedCellData = {display: '', bar: null};

    return {
      id: metric.company.id,
      company: metric.company.name,
      staff: names.length,
      staffNames: names,
      sensors: sensors.length,
      found: hasActiveSensors ? foundTotal : undefined,
      fixed: hasActiveSensors ? fixedTotal : undefined,
      fixedPercent: hasActiveSensors ? fixedPercent : undefined,
      resolvedAvg: hasActiveSensors ? avg : undefined,
      resolvedMin: hasActiveSensors ? min : undefined,
      resolvedMax: hasActiveSensors ? max : undefined,
      avgCell: hasActiveSensors ? {display: avg, bar: parentBar(avg)} : emptyCell,
      minCell: hasActiveSensors ? {display: min, bar: parentBar(min)} : emptyCell,
      maxCell: hasActiveSensors ? {display: max, bar: parentBar(max)} : emptyCell,
      subRows: subRows.length ? subRows : undefined,
    } as unknown as TanStackTableData;
  });
